import { useInitiallyHiddenElement } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { StatusIcon } from '../../icons/StatusIcons';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { AlertBaseColorStyleName, AlertType as InlineAlertType } from '../AlertStyles';

export { InlineAlertType };

const AlertTestId: string = 'InlineAlert';
const IconClass: string = 'statusIcon';
export const ContentClass: string = 'contentContainer';
export const MessageClass: string = 'alertMessage';

const AlertBaseStyleName: string = TypeStyle.style({
  borderRadius: 2,
  borderLeft: `4px solid transparent`,
  boxSizing: 'border-box',
  color: TabStyles.Typography.Color.Primary,
  display: 'flex',
  fontSize: '12px',
  letterSpacing: '0.2px',
  lineHeight: 1.5,
  $nest: {
    [`& .${IconClass}`]: {
      flexShrink: 0,
      marginLeft: 8,
      marginTop: 5,
    },
    [`& .${ContentClass}`]: {
      display: 'flex',
      flexGrow: 1,
    },
    '& button': {
      margin: 0, // reset safari 2px side margin on button elements
    },
  },
});

export interface InlineAlertBaseProps {
  interactiveElement?: React.ReactNode;
  /**
   * Test id to be attached to the root element of the InlineAlertWidget.
   * The suffix '-InlineAlert' will be automatically added
   */
  testId: string;
  /**
   * The `type` of InlineAlert you choose should correspond to the type of message it conveys.
   * It will also affect the style of the InlineAlert
   */
  type: InlineAlertType;
}

export const InlineAlert = React.forwardRef<HTMLDivElement, PropsAndElementAttrs<InlineAlertBaseProps>>((props, ref) => {
  const { children, className, interactiveElement, testId, type, ...htmlAttrs } = props;
  const hidden = useInitiallyHiddenElement();

  return (
    <div
      data-tb-test-id={makeTestId(testId, AlertTestId)}
      {...htmlAttrs}
      className={TypeStyle.classes(AlertBaseColorStyleName, AlertBaseStyleName, className, type)}
      ref={ref}
      role='status'
    >
      <StatusIcon type={type} className={IconClass} aria-hidden={hidden} />
      <div key='contentContainer' className={ContentClass} aria-hidden={hidden}>
        <div className={MessageClass}>{children}</div>
        {interactiveElement}
      </div>
    </div>
  );
});
InlineAlert.displayName = 'InlineAlert';
